export type Locale = 'en' | 'es' | 'pt';

export interface Translations {
  total: string;
  selectCountry: string;
  chooseCountry: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  continueToPayment: string;
  processing: string;
  required: string;
  termsText: string;
  termsOfService: string;
  privacyPolicy: string;
  and: string;
  poweredBy: string;
  loading: string;
  // Waiting state
  waitingTitle: string;
  waitingDescription: string;
  openCheckoutAgain: string;
  // Completed state
  completedTitle: string;
  completedDescription: string;
  // Validation errors
  errorCountryRequired: string;
  errorFirstNameRequired: string;
  errorLastNameRequired: string;
  errorEmailRequired: string;
  errorEmailInvalid: string;
  errorPhoneRequired: string;
  errorPhoneInvalid: string;
  // Placeholders
  placeholderFirstName: string;
  placeholderLastName: string;
  placeholderEmail: string;
  placeholderPhone: string;
}

const en: Translations = {
  total: 'Total',
  selectCountry: 'Select Country',
  chooseCountry: 'Choose your country',
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  phone: 'Phone Number',
  continueToPayment: 'Continue to Payment',
  processing: 'Processing...',
  required: '*',
  termsText: 'By completing this booking, you agree to the',
  termsOfService: 'Terms of Service',
  privacyPolicy: 'Privacy Policy',
  and: 'and',
  poweredBy: 'Powered by',
  loading: 'Loading...',
  // Waiting state
  waitingTitle: 'Waiting for payment',
  waitingDescription: 'Complete your payment in the new tab that was opened. This page will update automatically.',
  openCheckoutAgain: 'Open checkout again',
  // Completed state
  completedTitle: 'Payment completed',
  completedDescription: 'Your payment has been processed successfully. Thank you for your purchase!',
  // Validation errors
  errorCountryRequired: 'Please select a country',
  errorFirstNameRequired: 'First name is required',
  errorLastNameRequired: 'Last name is required',
  errorEmailRequired: 'Email is required',
  errorEmailInvalid: 'Please enter a valid email',
  errorPhoneRequired: 'Phone number is required',
  errorPhoneInvalid: 'Please enter a valid phone number',
  // Placeholders
  placeholderFirstName: 'Enter your first name',
  placeholderLastName: 'Enter your last name',
  placeholderEmail: 'Enter your email',
  placeholderPhone: '+1 (555) 000-0000',
};

const es: Translations = {
  total: 'Total',
  selectCountry: 'Seleccionar País',
  chooseCountry: 'Elige tu país',
  firstName: 'Nombre',
  lastName: 'Apellido',
  email: 'Correo Electrónico',
  phone: 'Número de Teléfono',
  continueToPayment: 'Continuar al Pago',
  processing: 'Procesando...',
  required: '*',
  termsText: 'Al completar esta reserva, aceptas los',
  termsOfService: 'Términos de Servicio',
  privacyPolicy: 'Política de Privacidad',
  and: 'y la',
  poweredBy: 'Powered by',
  loading: 'Cargando...',
  // Waiting state
  waitingTitle: 'Esperando pago',
  waitingDescription: 'Completa tu pago en la nueva pestaña que se abrió. Esta página se actualizará automáticamente.',
  openCheckoutAgain: 'Abrir checkout de nuevo',
  // Completed state
  completedTitle: 'Pago completado',
  completedDescription: 'Tu pago ha sido procesado exitosamente. ¡Gracias por tu compra!',
  // Validation errors
  errorCountryRequired: 'Por favor selecciona un país',
  errorFirstNameRequired: 'El nombre es requerido',
  errorLastNameRequired: 'El apellido es requerido',
  errorEmailRequired: 'El correo es requerido',
  errorEmailInvalid: 'Por favor ingresa un correo válido',
  errorPhoneRequired: 'El teléfono es requerido',
  errorPhoneInvalid: 'Por favor ingresa un teléfono válido',
  // Placeholders
  placeholderFirstName: 'Ingresa tu nombre',
  placeholderLastName: 'Ingresa tu apellido',
  placeholderEmail: 'Ingresa tu correo',
  placeholderPhone: '+1 (555) 000-0000',
};

const pt: Translations = {
  total: 'Total',
  selectCountry: 'Selecionar País',
  chooseCountry: 'Escolha seu país',
  firstName: 'Nome',
  lastName: 'Sobrenome',
  email: 'E-mail',
  phone: 'Número de Telefone',
  continueToPayment: 'Continuar para Pagamento',
  processing: 'Processando...',
  required: '*',
  termsText: 'Ao completar esta reserva, você concorda com os',
  termsOfService: 'Termos de Serviço',
  privacyPolicy: 'Política de Privacidade',
  and: 'e a',
  poweredBy: 'Powered by',
  loading: 'Carregando...',
  // Waiting state
  waitingTitle: 'Aguardando pagamento',
  waitingDescription: 'Complete seu pagamento na nova aba que foi aberta. Esta página será atualizada automaticamente.',
  openCheckoutAgain: 'Abrir checkout novamente',
  // Completed state
  completedTitle: 'Pagamento concluído',
  completedDescription: 'Seu pagamento foi processado com sucesso. Obrigado pela sua compra!',
  // Validation errors
  errorCountryRequired: 'Por favor selecione um país',
  errorFirstNameRequired: 'O nome é obrigatório',
  errorLastNameRequired: 'O sobrenome é obrigatório',
  errorEmailRequired: 'O e-mail é obrigatório',
  errorEmailInvalid: 'Por favor insira um e-mail válido',
  errorPhoneRequired: 'O telefone é obrigatório',
  errorPhoneInvalid: 'Por favor insira um telefone válido',
  // Placeholders
  placeholderFirstName: 'Insira seu nome',
  placeholderLastName: 'Insira seu sobrenome',
  placeholderEmail: 'Insira seu e-mail',
  placeholderPhone: '+55 (11) 99999-9999',
};

const translations: Record<Locale, Translations> = { en, es, pt };

export function getTranslations(locale: Locale = 'en'): Translations {
  return translations[locale] || translations.en;
}
